import { ArrowLeft, ChevronRight, Lock } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useHealthPassCard } from "../../hooks/useHealthPassCard";
import {
  walletEntryCount,
  walletItemLabel,
} from "../../components/card/cardStyle";
import { useI18nStore } from "../../stores/i18nStore";

const WALLET_ROUTES: Record<string, string> = {
  emergency: "/traveler/health/emergency",
  medications: "/traveler/medications",
  vaccinations: "/traveler/immunizations",
  checkin: "/traveler/health/checkin",
  appointments: "/traveler/health/appointment",
  insurance: "/traveler/health/insurance-request",
  travel_docs: "/traveler/documents",
  lab_results: "/traveler/health",
};

const WalletPage: React.FC = () => {
  const { t } = useI18nStore();
  const navigate = useNavigate();
  const { walletItems, isLoading } = useHealthPassCard();

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/traveler/card")}
          aria-label={t.back}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-xl font-bold text-gray-800">{t.cardWallet}</h1>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden divide-y divide-gray-100">
          {walletItems.map((item) => {
            const label = walletItemLabel(item, t);
            const route = WALLET_ROUTES[item.id] ?? "/traveler/card";

            return (
              <button
                key={item.id}
                type="button"
                disabled={!item.available}
                onClick={() => {
                  if (item.available) navigate(route);
                }}
                className={`w-full flex items-center gap-4 px-4 py-4 text-left ${
                  item.available
                    ? "cursor-pointer hover:bg-gray-50"
                    : "opacity-60 cursor-not-allowed"
                } transition-colors`}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-xl flex-shrink-0">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 text-sm">
                    {label}
                  </div>
                  {item.status === "locked" ? (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {t.upgradeForWalletItem.replace(
                        "{tier}",
                        item.requiredTier,
                      )}
                    </div>
                  ) : item.count !== null && item.count > 0 ? (
                    <div className="text-xs text-emerald-600 mt-0.5">
                      ✓ {walletEntryCount(item.count, t)}
                    </div>
                  ) : item.status === "empty" ? (
                    <div className="text-xs text-gray-400 mt-0.5">
                      {t.cardxNoDataYet}
                    </div>
                  ) : (
                    <div className="text-xs text-emerald-600 mt-0.5">
                      ✓ {t.cardxStatusComplete}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {item.status === "locked" ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {item.requiredTier}
                      </span>
                      <Lock className="w-4 h-4 text-gray-400" />
                    </div>
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WalletPage;
